import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Globe, FileText, Users, Briefcase, Shield, Copy } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const FORMS = [
  {
    key: 'referral',
    title: 'Public Referral Form',
    icon: Users,
    endpoint: `${API_BASE}/api/public/referral`,
    method: 'POST',
    fields: [
      { name: 'participantName', type: 'string', required: true },
      { name: 'email', type: 'string', required: false },
      { name: 'phone', type: 'string', required: false },
      { name: 'fundingType', type: 'NDIS | PRIVATE', required: false },
      { name: 'ndisNumber', type: 'string', required: false },
      { name: 'supportNeeds', type: 'string', required: false },
      { name: 'urgencyLevel', type: 'LOW | MEDIUM | HIGH', required: false },
      { name: 'referrerName', type: 'string', required: false },
      { name: 'referrerEmail', type: 'string', required: false },
      { name: 'referrerOrganisation', type: 'string', required: false },
    ],
    target: 'referrals',
  },
  {
    key: 'enquiry',
    title: 'Public Enquiry Form',
    icon: FileText,
    endpoint: `${API_BASE}/api/public/enquiry`,
    method: 'POST',
    fields: [
      { name: 'name', type: 'string', required: true },
      { name: 'email', type: 'string', required: false },
      { name: 'phone', type: 'string', required: false },
      { name: 'fundingType', type: 'NDIS | PRIVATE | UNKNOWN', required: false },
      { name: 'supportNeeds', type: 'string', required: false },
      { name: 'location', type: 'string', required: false },
      { name: 'message', type: 'string', required: false },
    ],
    target: 'enquiries',
  },
  {
    key: 'job-application',
    title: 'Public Job Application',
    icon: Briefcase,
    endpoint: `${API_BASE}/api/public/job-application`,
    method: 'POST',
    fields: [
      { name: 'firstName', type: 'string', required: true },
      { name: 'lastName', type: 'string', required: true },
      { name: 'email', type: 'string', required: true },
      { name: 'phone', type: 'string', required: false },
      { name: 'positionId', type: 'string', required: true },
      { name: 'coverLetter', type: 'string', required: false },
    ],
    target: 'candidates',
  },
];

function FormCard({ form }) {
  const Icon = form.icon;
  const copyUrl = () => { navigator.clipboard.writeText(form.endpoint); toast.success('URL copied'); };

  return (
    <Card data-testid={`form-card-${form.key}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-lg"><Icon className="w-5 h-5" />{form.title}</CardTitle>
          <Badge variant="outline" className="text-green-600 border-green-600">Active</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="text-xs text-muted-foreground mb-1">API Endpoint</p>
          <div className="flex items-center gap-2">
            <code className="text-xs bg-muted px-2 py-1 rounded flex-1 truncate">{form.method} {form.endpoint}</code>
            <Button size="sm" variant="ghost" onClick={copyUrl} data-testid={`copy-url-${form.key}`}><Copy className="w-3 h-3" /></Button>
          </div>
        </div>
        <div>
          <p className="text-xs text-muted-foreground mb-1">Creates record in: <span className="font-medium">{form.target}</span></p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground mb-2">Fields</p>
          <div className="grid gap-1">
            {form.fields.map(f => (
              <div key={f.name} className="flex items-center justify-between text-xs">
                <span className="font-mono">{f.name}</span>
                <div className="flex gap-2">
                  <span className="text-muted-foreground">{f.type}</span>
                  {f.required && <Badge variant="destructive" className="text-[10px] px-1">Required</Badge>}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="pt-2 border-t">
          <p className="text-xs text-muted-foreground flex items-center gap-1"><Shield className="w-3 h-3" />Spam protection: Honeypot field + Rate limiting (10/5min per IP)</p>
        </div>
      </CardContent>
    </Card>
  );
}

export default function PublicFormManagementPage() {
  return (
    <div className="space-y-6" data-testid="public-forms-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Globe className="w-6 h-6" />Public Web Forms</h1>
      </div>
      <p className="text-muted-foreground">These endpoints accept unauthenticated form submissions from your public website. Add a hidden <code className="text-xs bg-muted px-1 rounded">website</code> field as honeypot — bots that fill it are silently ignored.</p>
      <div className="grid gap-4 lg:grid-cols-3">
        {FORMS.map(f => <FormCard key={f.key} form={f} />)}
      </div>
    </div>
  );
}
